
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from './Icons';
import { UserProfile, Organization } from '../../types';

interface AppHeaderProps {
  user: UserProfile;
  organization: Organization | null;
  onLogout: () => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  /** If provided, called for tab navigation instead of using navigate() */
  onTabChange?: (tab: string) => void;
}

export const AppHeader = ({ user, organization, onLogout, activeTab, setActiveTab, onTabChange }: AppHeaderProps) => {
  const navigate = useNavigate();
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (onTabChange) {
      onTabChange(tab);
    } else {
      navigate(`/dashboard?tab=${tab}`);
    }
  };

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
        <button
          onClick={() => handleTabChange('browse')}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <div className="bg-primary text-white p-2 rounded-lg">
            <Icons.BookOpen className="w-6 h-6" />
          </div>
          <div className="flex flex-col">
            <span className="font-serif font-bold text-xl text-gray-800 tracking-tight leading-none">Bible Aware</span>
            {organization && <span className="text-xs text-gray-500 font-medium">{organization.name}</span>}
          </div>
        </button>

        <div className="hidden md:flex items-center gap-6">
          <button
            onClick={() => handleTabChange('browse')}
            className={`font-medium text-sm transition-colors ${activeTab === 'browse' ? 'text-primary' : 'text-gray-500 hover:text-gray-800'}`}
          >
            Studies
          </button>
          <button
            onClick={() => handleTabChange('courses')}
            className={`font-medium text-sm transition-colors ${activeTab === 'courses' ? 'text-primary' : 'text-gray-500 hover:text-gray-800'}`}
          >
            Courses
          </button>
          {user.isAdmin && (
            <div className="relative">
              <button
                onClick={() => setAdminDropdownOpen(!adminDropdownOpen)}
                onBlur={() => setTimeout(() => setAdminDropdownOpen(false), 200)}
                className={`flex items-center gap-1.5 font-medium text-sm transition-colors ${
                  ['members', 'manage'].includes(activeTab)
                    ? 'text-primary'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <Icons.Shield className="w-4 h-4" />
                Admin
                <Icons.Next className={`w-3.5 h-3.5 transition-transform ${adminDropdownOpen ? 'rotate-90' : ''}`} />
              </button>

              {adminDropdownOpen && (
                <div className="absolute top-full mt-2 right-0 bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[220px] z-50">
                  <button
                    onClick={() => {
                      navigate('/manage');
                      setAdminDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex items-center gap-3 ${
                      activeTab === 'manage' ? 'bg-blue-50 text-primary font-medium' : 'text-gray-700'
                    }`}
                  >
                    <Icons.Edit className="w-4 h-4" />
                    <div>
                      <div className="font-medium">Content Manager</div>
                      <div className="text-xs text-gray-500">Create & edit studies/courses</div>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/admin/responses');
                      setAdminDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex items-center gap-3 text-gray-700"
                  >
                    <Icons.MessageSquare className="w-4 h-4" />
                    <div>
                      <div className="font-medium">Responses</div>
                      <div className="text-xs text-gray-500">Questions & CTA submissions</div>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      handleTabChange('members');
                      setAdminDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex items-center gap-3 ${
                      activeTab === 'members' ? 'bg-blue-50 text-primary font-medium' : 'text-gray-700'
                    }`}
                  >
                    <Icons.Users className="w-4 h-4" />
                    <div>
                      <div className="font-medium">Members</div>
                      <div className="text-xs text-gray-500">Manage roles & permissions</div>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/admin/media');
                      setAdminDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex items-center gap-3 text-gray-700"
                  >
                    <Icons.Image className="w-4 h-4" />
                    <div>
                      <div className="font-medium">Media Library</div>
                      <div className="text-xs text-gray-500">Manage images & videos</div>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/admin/analytics');
                      setAdminDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex items-center gap-3 text-gray-700"
                  >
                    <Icons.BarChart className="w-4 h-4" />
                    <div>
                      <div className="font-medium">Analytics</div>
                      <div className="text-xs text-gray-500">Content & learner insights</div>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/admin/ai-usage');
                      setAdminDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex items-center gap-3 text-gray-700"
                  >
                    <Icons.Star className="w-4 h-4" />
                    <div>
                      <div className="font-medium">AI Usage</div>
                      <div className="text-xs text-gray-500">View quota & activity</div>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/admin/notifications');
                      setAdminDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex items-center gap-3 text-gray-700"
                  >
                    <Icons.Mail className="w-4 h-4" />
                    <div>
                      <div className="font-medium">Notifications</div>
                      <div className="text-xs text-gray-500">Email settings for members</div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          )}
           <button
             onClick={() => handleTabChange('profile')}
             className={`font-medium text-sm transition-colors ${activeTab === 'profile' ? 'text-primary' : 'text-gray-500 hover:text-gray-800'}`}
          >
            My Journey
          </button>
        </div>

        <div className="flex items-center gap-4">
          <button onClick={onLogout} className="hidden md:flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors">
            <Icons.Logout className="w-4 h-4" />
            Sign Out
          </button>
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <Icons.X className="w-6 h-6" />
            ) : (
              <Icons.Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-2 space-y-1">
            <button
              onClick={() => {
                handleTabChange('browse');
                setMobileMenuOpen(false);
              }}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
                activeTab === 'browse'
                  ? 'bg-blue-50 text-primary'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <Icons.BookOpen className="w-5 h-5" />
                <span>Studies</span>
              </div>
            </button>
            <button
              onClick={() => {
                handleTabChange('courses');
                setMobileMenuOpen(false);
              }}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
                activeTab === 'courses'
                  ? 'bg-blue-50 text-primary'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <Icons.List className="w-5 h-5" />
                <span>Courses</span>
              </div>
            </button>
            {user.isAdmin && (
              <>
                <button
                  onClick={() => {
                    navigate('/manage');
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
                    activeTab === 'manage'
                      ? 'bg-blue-50 text-primary'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icons.Edit className="w-5 h-5" />
                    <span>Content Manager</span>
                  </div>
                </button>
                <button
                  onClick={() => {
                    navigate('/admin/responses');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 rounded-lg font-medium text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Icons.MessageSquare className="w-5 h-5" />
                    <span>Responses</span>
                  </div>
                </button>
                <button
                  onClick={() => {
                    handleTabChange('members');
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
                    activeTab === 'members'
                      ? 'bg-blue-50 text-primary'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icons.Users className="w-5 h-5" />
                    <span>Members</span>
                  </div>
                </button>
                <button
                  onClick={() => {
                    navigate('/admin/media');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 rounded-lg font-medium text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Icons.Image className="w-5 h-5" />
                    <span>Media Library</span>
                  </div>
                </button>
                <button
                  onClick={() => {
                    navigate('/admin/analytics');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 rounded-lg font-medium text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Icons.BarChart className="w-5 h-5" />
                    <span>Analytics</span>
                  </div>
                </button>
                <button
                  onClick={() => {
                    navigate('/admin/ai-usage');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 rounded-lg font-medium text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Icons.Star className="w-5 h-5" />
                    <span>AI Usage</span>
                  </div>
                </button>
                <button
                  onClick={() => {
                    navigate('/admin/notifications');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 rounded-lg font-medium text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Icons.Mail className="w-5 h-5" />
                    <span>Notifications</span>
                  </div>
                </button>
              </>
            )}
            <button
              onClick={() => {
                handleTabChange('profile');
                setMobileMenuOpen(false);
              }}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
                activeTab === 'profile'
                  ? 'bg-blue-50 text-primary'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <Icons.User className="w-5 h-5" />
                <span>My Journey</span>
              </div>
            </button>

            {/* Mobile-only items */}
            <div className="pt-2 mt-2 border-t border-gray-200">
              <button
                onClick={() => {
                  onLogout();
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-4 py-3 rounded-lg font-medium text-sm text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Icons.Logout className="w-5 h-5" />
                  <span>Sign Out</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
